/**
 * Escape a literal for use inside single quotes in a Drive v3 `q` string.
 * Google requires `'` → `''` (see Drive search query syntax).
 */
export const escapeDriveQueryValue = (s: string) => (s || '').replace(/'/g, "''");

export const getOrCreateDriveFolder = async (
  token: string,
  folderName: string,
  parentId?: string
) => {
  const safeName = escapeDriveQueryValue(folderName);
  const safeParent = parentId ? escapeDriveQueryValue(parentId) : '';
  let query = `mimeType='application/vnd.google-apps.folder' and name='${safeName}' and trashed=false`;
  if (safeParent) query += ` and '${safeParent}' in parents`;

  const searchRes = await fetch(
    `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&spaces=drive`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const searchData = await searchRes.json();

  if (searchData.files && searchData.files.length > 0) return searchData.files[0].id;

  const metadata = {
    name: folderName,
    mimeType: 'application/vnd.google-apps.folder',
    ...(parentId ? { parents: [parentId] } : {}),
  };
  const createRes = await fetch('https://www.googleapis.com/drive/v3/files', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(metadata),
  });
  const createData = await createRes.json();
  return createData.id;
};

/** Create an empty Google Doc in Drive (teacher pastes or uses Docs tools to build content). */
export const createGoogleDocInDrive = async (
  token: string,
  title: string,
  folderId?: string
): Promise<{ id: string; webViewLink?: string }> => {
  const metadata = {
    name: title,
    mimeType: 'application/vnd.google-apps.document',
    ...(folderId ? { parents: [folderId] } : {}),
  };
  const res = await fetch(
    'https://www.googleapis.com/drive/v3/files?fields=id,name,webViewLink,mimeType',
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(metadata),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      `Drive Doc Error: ${(err as { error?: { message?: string } }).error?.message || res.statusText}`
    );
  }
  return res.json() as Promise<{ id: string; webViewLink?: string }>;
};

export const uploadTextFileToDrive = async (
  token: string,
  textContent: string,
  fileName: string,
  folderId: string,
  mimeType: 'text/html' | 'text/plain' = 'text/html'
) => {
  const boundary = '-------314159265358979323847';
  const delimiter = `\r\n--${boundary}\r\n`;
  const closeDelim = `\r\n--${boundary}--`;
  const metadata = JSON.stringify({
    name: fileName,
    parents: [folderId],
    mimeType,
  });
  const multipartRequestBody = `${delimiter}Content-Type: application/json; charset=UTF-8\r\n\r\n${metadata}${delimiter}Content-Type: ${mimeType}; charset=UTF-8\r\n\r\n${textContent}${closeDelim}`;

  const res = await fetch(
    'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,webViewLink,mimeType',
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': `multipart/related; boundary=${boundary}`,
      },
      body: multipartRequestBody,
    }
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      `Drive Upload Error: ${(err as { error?: { message?: string } }).error?.message || res.statusText}`
    );
  }
  return res.json() as Promise<{ id?: string; webViewLink?: string }>;
};
